/**
 * The mail account card on the Settings page: the SMTP side that sends invoices, and the POP3 side
 * that reads what comes back.
 *
 * The port of `MailAccountViewModel` and its card in `SettingsPage.xaml`.
 *
 *   * ITS OWN RECORD AND ITS OWN SAVE. The tunables below are a plaintext document returned to any
 *     staff user and broadcast to every agent; this carries two encrypted passwords, so it is never
 *     part of that document and never rides out with an unrelated save.
 *   * IT NEVER HOLDS A PASSWORD IT DID NOT JUST TYPE. The server says only whether one is stored, so
 *     both boxes start empty and an empty box means "keep it"; clearing one is its own tick. The three
 *     readings the server understands are null (keep), "" (clear) and anything else (replace).
 *   * ONE REQUEST BUILDER FOR SAVE AND TEST. A probe that resolved the form even slightly differently
 *     from the save would pass on a configuration that then fails once stored.
 *   * The two channels report on their own lines - "sending is fine, the POP3 password is wrong" is the
 *     answer a single summary cannot give.
 */
import { TextArea, type Control } from 'webwin';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import {
  MailSecurity,
  type MailAccountDto,
  type MailTestRequest,
  type MailTestResult,
  type UpdateMailAccountRequest
} from '../../contracts.gen.js';
import { localDateTime } from '../../format.js';
import type { Card } from '../../ui/Card.js';
import { StatusLine } from '../../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkLine,
  comboBox,
  field,
  fieldRow,
  FONT,
  GAP,
  h2,
  muted,
  passwordBox,
  relayout,
  stackOf,
  text,
  textBox,
  vstack,
  wrappingCheck
} from '../../ui/layout.js';
import type { PageContext } from '../page.js';

/** `MailTemplates.Placeholders` - a static readonly array, so ContractsGen cannot carry it. */
const PLACEHOLDERS = [
  '{InvoiceNumber}',
  '{CustomerName}',
  '{ContactName}',
  '{Total}',
  '{IssueDate}',
  '{DueDate}',
  '{DueLine}',
  '{PayLink}',
  '{PayLine}',
  '{SenderName}'
];

/** In `MailSecurity` order. Automatic reads the port: 465 is implicit TLS, anything else STARTTLS when offered. */
const SECURITY = ['Automatic', 'None (plain)', 'STARTTLS', 'Implicit TLS'] as const;

export interface MailAccountSection {
  card: Card;
  load(): Promise<void>;
}

function portOf(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n >= 1 && n <= 65535 ? n : null;
}

function secret(typed: string, clear: boolean): string | null {
  if (clear) return '';
  return typed.length > 0 ? typed : null;
}

/** Where the account is read and written: the organization's own, or the host's. */
export interface MailAccountApi {
  get(signal?: AbortSignal): Promise<MailAccountDto>;
  update(req: UpdateMailAccountRequest): Promise<MailAccountDto>;
  test(req: MailTestRequest): Promise<MailTestResult>;
}

export interface MailAccountOptions {
  api?: MailAccountApi;
  /** Only the SMTP half: no POP3, no ticket-from-mail, no invoice templates. */
  sendOnly?: boolean;
  title?: string;
  description?: string;
}

const ORGANIZATION_MAIL: MailAccountApi = {
  get: signal => crm.getMailAccount(signal),
  update: req => crm.updateMailAccount(req),
  test: req => crm.testMail(req)
};

export function mailAccountSection(ctx: PageContext, canEdit: boolean, options: MailAccountOptions = {}): MailAccountSection {
  const api = options.api ?? ORGANIZATION_MAIL;
  const sendOnly = options.sendOnly ?? false;
  // ---- Sending ----
  const sendingEnabled = wrappingCheck('Send email from this CRM');
  const smtpHost = textBox({ placeholder: 'smtp.example.com' });
  const smtpPort = textBox({ text: '587' });
  const smtpSecurity = comboBox<string>(SECURITY, { selectedIndex: MailSecurity.Auto });
  const smtpUser = textBox();
  const smtpPassword = passwordBox();
  const smtpPasswordState = muted('');
  const clearSmtpPassword = wrappingCheck('Clear the stored sending password');
  const fromAddress = textBox({ placeholder: 'billing@yourcompany.com' });
  const fromName = textBox();
  const replyTo = textBox();

  // ---- Receiving ----
  const receivingEnabled = wrappingCheck('Read replies and delivery failures from this mailbox');
  const pop3Host = textBox({ placeholder: 'pop.example.com' });
  const pop3Port = textBox({ text: '995' });
  const pop3Security = comboBox<string>(SECURITY, { selectedIndex: MailSecurity.Auto });
  const pop3User = textBox();
  const pop3Password = passwordBox();
  const pop3PasswordState = muted('');
  const clearPop3Password = wrappingCheck('Clear the stored receiving password');
  const deleteAfterFetch = wrappingCheck('Delete messages from the server after reading them');
  const createTickets = wrappingCheck('Open a ticket from mail sent by a known customer contact');
  const lastPoll = muted('');

  // ---- Shared ----
  const allowInvalidCertificate = wrappingCheck('Accept an untrusted TLS certificate');
  const subjectBox = textBox();
  const bodyBox = new TextArea({ height: 170, hAlign: 'stretch', wrap: true });
  const testTo = textBox({ placeholder: 'you@yourcompany.com' });

  const status = new StatusLine();
  const smtpVerdict = muted('');
  const pop3Verdict = muted('');
  const saveButton = button('Save mail settings', () => void save(), true);
  const testButton = button('Test connection', () => void test());

  const editable = [
    sendingEnabled, smtpHost, smtpPort, smtpSecurity, smtpUser, smtpPassword, clearSmtpPassword,
    fromAddress, fromName, replyTo, receivingEnabled, pop3Host, pop3Port, pop3Security, pop3User,
    pop3Password, clearPop3Password, deleteAfterFetch, createTickets, allowInvalidCertificate,
    subjectBox, bodyBox, testTo, saveButton, testButton
  ];
  for (const c of editable) c.enabled = canEdit;

  const shell = card();
  const receivingPart: Control[] = sendOnly
    ? []
    : [
          text('Receiving (POP3)', { font: FONT.bold }),
          checkLine(receivingEnabled),
          field('Server', pop3Host),
          fieldRow(field('Port', pop3Port), field('Security', pop3Security)),
          fieldRow(field('Sign-in name', pop3User), field('Password', pop3Password)),
          pop3PasswordState,
          checkLine(clearPop3Password),
          checkLine(deleteAfterFetch),
          checkLine(createTickets),
          muted(
            'Only senders the CRM already recognizes - a customer\'s billing contact, one of their employees, or an approved portal account. Anything else is recorded and left alone.'
          ),
          lastPoll
      ];
  const templatePart: Control[] = sendOnly
    ? []
    : [field('Invoice subject', subjectBox), field('Invoice message', bodyBox), muted('Placeholders: ' + PLACEHOLDERS.join(' '))];
  shell.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      h2(options.title ?? 'Mail account'),
      muted(
        options.description ??
          'One mailbox for the whole CRM: SMTP sends invoices, POP3 reads what comes back - replies are filed against the invoice, delivery failures flag it, and mail from a customer contact can open a ticket. Passwords are stored encrypted and are never sent back to this page.'
      ),

      text('Sending (SMTP)', { font: FONT.bold }),
      checkLine(sendingEnabled),
      field('Server', smtpHost),
      fieldRow(field('Port', smtpPort), field('Security', smtpSecurity)),
      fieldRow(field('Sign-in name', smtpUser), field('Password', smtpPassword)),
      smtpPasswordState,
      checkLine(clearSmtpPassword),
      fieldRow(field('From address', fromAddress), field('From name', fromName)),
      field('Reply-to (optional)', replyTo),

      ...receivingPart,

      checkLine(allowInvalidCertificate),
      muted(
        'For a self-signed mail server on your own network. Leave this off for anything reached over the internet - there it hides exactly the failure it looks like it is fixing.'
      ),
      ...templatePart,
      // Offered because a login-only probe can report a false pass: several providers accept the
      // sign-in and then refuse the message as an unauthorized sender.
      field('Send a test message to (optional)', testTo),
      stackOf(buttonRow(), saveButton, testButton),
      smtpVerdict,
      pop3Verdict,
      status.label
    )
  );

  function showVerdict(label: typeof smtpVerdict, value: string): void {
    label.text = value;
    label.visible = value.length > 0;
  }

  function bind(a: MailAccountDto): void {
    sendingEnabled.checked = a.sendingEnabled;
    smtpHost.text = a.smtpHost ?? '';
    smtpPort.text = String(a.smtpPort);
    smtpSecurity.selectedIndex = a.smtpSecurity;
    smtpUser.text = a.smtpUsername ?? '';
    fromAddress.text = a.fromAddress ?? '';
    fromName.text = a.fromName ?? '';
    replyTo.text = a.replyToAddress ?? '';

    receivingEnabled.checked = a.receivingEnabled;
    pop3Host.text = a.pop3Host ?? '';
    pop3Port.text = String(a.pop3Port);
    pop3Security.selectedIndex = a.pop3Security;
    pop3User.text = a.pop3Username ?? '';
    deleteAfterFetch.checked = a.deleteAfterFetch;
    createTickets.checked = a.createTicketsFromMail;

    allowInvalidCertificate.checked = a.allowInvalidCertificate;
    subjectBox.text = a.invoiceSubjectTemplate ?? '';
    bodyBox.text = a.invoiceBodyTemplate ?? '';

    // The stored passwords never come back, and a "clear" tick left on after a save would clear the
    // password again next time.
    smtpPassword.text = '';
    pop3Password.text = '';
    clearSmtpPassword.checked = false;
    clearPop3Password.checked = false;

    const state = (stored: boolean): string =>
      stored
        ? 'A password is stored. Leave the box empty to keep it.'
        : 'No password stored. Leave the box empty if this server takes none.';
    smtpPasswordState.text = state(a.hasSmtpPassword);
    pop3PasswordState.text = state(a.hasPop3Password);

    // The line that makes a mailbox which has quietly stopped answering visible, instead of it
    // presenting as "customers have stopped replying".
    if (!a.receivingEnabled) lastPoll.text = '';
    else if (!a.lastPollUtc) lastPoll.text = 'The mailbox has not been checked yet.';
    else
      lastPoll.text = a.lastPollError
        ? `Last check failed at ${localDateTime(a.lastPollUtc)}: ${a.lastPollError}`
        : `Last checked ${localDateTime(a.lastPollUtc)} - ${a.lastPollMessages} new message(s).`;
    lastPoll.role = a.receivingEnabled && a.lastPollError ? 'danger' : 'muted';
    lastPoll.visible = lastPoll.text.length > 0;
    relayout(shell);
  }

  function buildRequest(): UpdateMailAccountRequest | string {
    const sPort = portOf(smtpPort.text);
    if (sPort === null) return 'The SMTP port must be a whole number between 1 and 65535.';
    const pPort = portOf(pop3Port.text);
    if (pPort === null) return 'The POP3 port must be a whole number between 1 and 65535.';

    return {
      sendingEnabled: sendingEnabled.checked,
      smtpHost: smtpHost.text,
      smtpPort: sPort,
      smtpSecurity: smtpSecurity.selectedIndex as MailSecurity,
      smtpUsername: smtpUser.text,
      smtpPassword: secret(smtpPassword.text, clearSmtpPassword.checked),
      fromAddress: fromAddress.text,
      fromName: fromName.text,
      replyToAddress: replyTo.text,
      receivingEnabled: receivingEnabled.checked,
      pop3Host: pop3Host.text,
      pop3Port: pPort,
      pop3Security: pop3Security.selectedIndex as MailSecurity,
      pop3Username: pop3User.text,
      pop3Password: secret(pop3Password.text, clearPop3Password.checked),
      deleteAfterFetch: deleteAfterFetch.checked,
      createTicketsFromMail: createTickets.checked,
      allowInvalidCertificate: allowInvalidCertificate.checked,
      invoiceSubjectTemplate: subjectBox.text,
      invoiceBodyTemplate: bodyBox.text
    };
  }

  function setBusy(busy: boolean): void {
    saveButton.enabled = canEdit && !busy;
    testButton.enabled = canEdit && !busy;
  }

  async function save(): Promise<void> {
    const req = buildRequest();
    if (typeof req === 'string') {
      status.error(req);
      return;
    }
    setBusy(true);
    status.busy('Saving…');
    try {
      const saved = await api.update(req);
      if (ctx.cancelled()) return;
      // Re-bound to what came back: the server is the only thing that knows whether a password survived.
      bind(saved);
      status.ok('Mail settings saved.');
    } catch (error) {
      if (!ctx.cancelled()) status.error('Could not save the mail settings: ' + describeError(error));
    } finally {
      setBusy(false);
    }
  }

  async function test(): Promise<void> {
    const req = buildRequest();
    if (typeof req === 'string') {
      status.error(req);
      return;
    }
    setBusy(true);
    status.busy('Testing…');
    showVerdict(smtpVerdict, '');
    showVerdict(pop3Verdict, '');
    const result = await api.test({
      settings: req,
      testSmtp: sendingEnabled.checked,
      testPop3: !sendOnly && receivingEnabled.checked,
      sendTestTo: testTo.text.trim() || null
    });
    setBusy(false);
    if (ctx.cancelled()) return;
    showVerdict(smtpVerdict, result.smtp.checked ? 'Sending: ' + result.smtp.detail : '');
    showVerdict(pop3Verdict, result.pop3.checked ? 'Receiving: ' + result.pop3.detail : '');
    smtpVerdict.role = result.smtp.checked && !result.smtp.ok ? 'danger' : 'muted';
    pop3Verdict.role = result.pop3.checked && !result.pop3.ok ? 'danger' : 'muted';
    // Only the summary is coloured; the channel lines say which half it was.
    if (result.success) status.ok(result.summary);
    else status.error(result.summary);
    relayout(shell);
  }

  showVerdict(smtpVerdict, '');
  showVerdict(pop3Verdict, '');

  return {
    card: shell,
    async load() {
      try {
        const a = await api.get(ctx.dispose.signal());
        if (!ctx.cancelled()) bind(a);
      } catch (error) {
        if (!ctx.cancelled()) status.error('Could not load the mail settings: ' + describeError(error));
      }
    }
  };
}
